import type { RefObject } from 'react';
import gsap from 'gsap';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import type { TextRef } from '../../../atoms/text/Text.data';
import { findTransitionController } from '../../../../util/core-transition-component';

interface AccordionItemRefs {
  elementRef?: RefObject<HTMLElement | null>;
  iconRef?: RefObject<HTMLDivElement | null>;
  headingRef?: RefObject<HeadingRef | null>;
  descriptionRef?: RefObject<TextRef | null>;
  lineRef?: RefObject<HTMLSpanElement | null>;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  { headingRef, descriptionRef, lineRef, iconRef }: AccordionItemRefs,
): void {
  const title = findTransitionController(headingRef);
  const description = findTransitionController(descriptionRef);

  if (lineRef?.current) {
    timeline.fromTo(
      lineRef.current,
      { scaleX: 0, transformOrigin: 'left center' },
      { duration: 0.5, ease: 'Quad.easeInOut', scaleX: 1 },
    );
  }

  if (iconRef?.current) {
    timeline.fromTo(
      iconRef.current,
      { opacity: 0, scale: 0.4 },
      {
        scale: 1,
        opacity: 1,
        ease: 'Quad.easeInOut',
        clearProps: 'scale, opacity',
        duration: 0.4,
      },
      0.3,
    );
  }

  if (title) {
    timeline.add(title.getTimeline('in') as gsap.core.Timeline, 0);
  }

  if (description) {
    timeline.add(description.getTimeline('in') as gsap.core.Timeline, 0.05);
  }
}

export function toggle(
  contentRef: RefObject<HTMLDivElement | null>,
  isOpen: boolean,
  callback: () => void,
  immediate: boolean = false,
): void {
  gsap.to(contentRef.current, {
    height: isOpen ? contentRef.current?.scrollHeight : 0,
    duration: immediate ? 0 : 0.6,
    ease: 'Quad.easeInOut',
    clearProps: isOpen ? 'height' : '',
    onComplete: () => {
      if (callback) {
        callback();
      }
    },
  });
}

export function toggleIconState(
  refs: {
    iconRef: RefObject<HTMLDivElement | null>;
    verticalLineRef: RefObject<HTMLSpanElement | null>;
  },
  isOpen: boolean,
): void {
  if (refs.iconRef) {
    gsap.to(refs.iconRef.current, {
      rotation: isOpen ? 180 : 0,
      duration: 0.4,
      ease: 'Power2.easeOut',
    });

    gsap.to(refs.verticalLineRef.current, {
      rotation: isOpen ? 90 : 0,
      duration: 0.5,
      ease: 'Power2.easeInOut',
    });
  }
}

export function setupMouseEnterTimeline(
  timeline: gsap.core.Timeline,
  { headingRef }: AccordionItemRefs,
): void {
  if (headingRef?.current) {
    timeline.fromTo(
      headingRef.current,
      { x: 0 },
      { duration: 0.4, ease: 'Power2.easeOut', x: 20 },
      0,
    );
  }
}

export function setupMouseLeaveTimeline(
  timeline: gsap.core.Timeline,
  { headingRef }: AccordionItemRefs,
): void {
  if (headingRef?.current) {
    timeline.to(headingRef.current, { duration: 0.4, ease: 'Power2.easeOut', x: 0 }, 0);
  }
}
